import { mapGetters } from 'vuex'
import { lowestPrice30d, lowestPriceLabelKind, showsLowestPrice } from '@/helpers/product'

/**
 * The § 12a caption that has to sit next to every announced discount.
 *
 * It renders wherever a discount is displayed and the backend has a 30-day low to report -
 * deliberately without comparing that low to the current price. The two are equal for all but
 * the first hour of a sale (see helpers/product/lowest-price), so a comparison would take the
 * caption off the site almost entirely, and off it permanently for anyone holding a personal
 * discount. That the low equals the price on screen is the ordinary case, not a defect.
 *
 * Styling is deliberately minimal - enough that the line does not read as a second price,
 * with the finished look still design's to give.
 */
export default {
  name: 'LowestPrice',
  props: {
    /**
     * @type {ProductPrice}
     */
    productPrice: {
      required: true,
      default: () => ({}),
    },
  },
  computed: {
    ...mapGetters({
      currentRegionCurrency: 'regions/currentRegionCurrency',
    }),
    visible() {
      return showsLowestPrice(this.productPrice)
    },
    formattedPrice() {
      const currency = this.productPrice.currency || this.currentRegionCurrency

      return lowestPrice30d(this.productPrice).toFixed(2) + ` ${this.$currency(currency)}`
    },
    caption() {
      if (lowestPriceLabelKind(this.productPrice) === 'full') {
        // `lowestPrice30dLabel` in the translation sheet, but the interceptor camel-cases the
        // keys of every response, this one included - so the loaded key is the one with the
        // capital D, exactly as with the price field itself (see helpers/product/lowest-price).
        // Every other digit-bearing key in this repo is already written this way
        // (`error404PageNotFound`, `from1To5`); asking for the sheet's spelling renders the
        // raw key next to the price.
        return this.$t('lowestPrice30DLabel').replace('{price}', this.formattedPrice)
      }

      // Shorter window: the date is what makes the shorter claim a true one, so without it
      // the wording would be back to the "30 days" it cannot support.
      return this.$t('lowestPriceSinceLabel')
        .replace('{price}', this.formattedPrice)
        .replace('{date}', this.formattedSince)
    },
    formattedSince() {
      const since = this.productPrice.priceHistorySince

      return since ? this.moment(since).format('D. M. YYYY') : ''
    },
  },
}
